/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Spinner } from 'reactstrap';
import { useParams, useNavigate } from 'react-router-dom';

import { Formik, Form } from 'formik';
import {
  FormikPasswordInput as PasswordInput,
  useTranslator,
  useToast,
  withMobileAppMode,
  useAppContext,
} from '@front-activerse/common-components';
import validationSchema from './validationSchema';

const AuthResetPasswordComponent = ({ mobileAppMode, resetPassword, validateToken, loading }) => {
  const { t } = useTranslator();
  const { toastError, toastSuccess } = useToast();
  const { token } = useParams();
  const navigate = useNavigate();
  const [errors, setErrors] = useState([]);
  const [tokenError, setTokenError] = useState(false);

  const initialValues = {
    password: '',
  };

  const {
    images: { eye, eyeSlash, removeAccountAlert },
  } = useAppContext();

  const validateUserToken = async () => {
    const response = await validateToken({ token });
    const { validateResetPasswordToken } = response.data.auth;

    try {
      if (!validateResetPasswordToken) {
        setTokenError(true);
      }
    } catch (e) {
      toastError();
      throw new Error(`Failed to validate reset password token, got error: ${e}`);
    }
  };

  useEffect(() => {
    validateUserToken();
  }, [token]);

  const handleSubmit = async (values, { resetForm }) => {
    setErrors([]);
    try {
      const response = await resetPassword({
        token,
        password: values.password,
      });
      const { code, details } = response.data.auth.resetPassword;

      if (code === 200) {
        toastSuccess(<p className="mb-0">{t('toastMessage.newPasswordSet')}</p>);
        navigate('/auth/login');
        resetForm();
      } else if (code === 422) {
        const errorsArray = [];
        for (let i = 0; i < details.length; i += 1) {
          const error = details[i];
          if (error.fieldName === 'password' || error.fieldName === 'token') {
            error.messages.forEach((message) => {
              errorsArray.push(message);
            });
          }
        }
        setErrors(errorsArray);
      }
    } catch (e) {
      toastError();
      throw new Error(`Failed to set new password, got error: ${e}`);
    }
  };

  return (
    <div className="page-template-auth full-height7">
      <Container className="py-xxl">
        <Row>
          <Col
            xs={12}
            md={{ size: 8, offset: 2 }}
            xl={{ size: 6, offset: 3 }}
            xxl={{ size: 4, offset: 4 }}
          >
            <h1 className="text-center title-medium semi-bold mb-xl">
              {t('setNewPassword.title')}
            </h1>
            <div className="page-container">
              <div className="auth-container">
                {tokenError ? (
                  <div className="text-center">
                    <img src={removeAccountAlert} className="mb-l" alt="" />
                    <p className="semi-bold mb-l">{t('linkExpiredNotification.title')}</p>
                    <p className="mb-0">{t('linkExpiredNotification.content')}</p>
                  </div>
                ) : (
                  <Formik
                    validationSchema={validationSchema}
                    validateOnChange
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                  >
                    {({ isValid }) => {
                      return (
                        <Form>
                          <PasswordInput
                            name="password"
                            type="password"
                            label={t('inputLabel.newPassword')}
                            iconEye={eye}
                            iconEyeSlash={eyeSlash}
                            translateErrMsg
                          />

                          {errors.length > 0 && (
                            <p className="error my-xl text-center">
                              {errors.map((error, idx) => (
                                <span className="d-block" key={idx}>
                                  {error}
                                </span>
                              ))}
                            </p>
                          )}

                          <div className="mt-xl">
                            <Button
                              type="submit"
                              color="primary"
                              className="w-100"
                              disabled={!isValid || loading}
                            >
                              {loading ? (
                                <>
                                  <Spinner size="sm" className="me-2">
                                    Loading...
                                  </Spinner>
                                  {t('setNewPassword.saving.button')}
                                </>
                              ) : (
                                t('setNewPassword.save.button')
                              )}
                            </Button>
                          </div>
                        </Form>
                      );
                    }}
                  </Formik>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default withMobileAppMode(AuthResetPasswordComponent);
